import React from 'react';
import { useParams } from 'react-router-dom';
import { useApolloClient, useQuery, useMutation } from '@apollo/react-hooks';
import { SelectItem, SelectItemLoader, Toast } from 'components';
import {
  GroupPeopleQuery,
  AddMemberModalQuery,
  GroupGetStartedConditionQuery,
  GroupGetStartedFeedQuery,
} from 'services/apollo/queries';
import styles from './AddGroupMemberModal.module.scss';
import { addGroupMemberModalMutation } from 'services/apollo/mutations';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import produce from 'immer';
import { toast } from 'react-toastify';
import { useTranslation } from 'hooks';
import { Logger } from 'services';
import { useGroupGetStartedState, useFeatureFlagState } from 'context';
import { GroupTypes } from 'helpers';

const ANIMATION_DURATION = 0.8 * 1000;
interface AddGroupMemberModalProps {
  onCancel: any;
}

export const AddGroupMemberModal: React.FC<AddGroupMemberModalProps> = ({ onCancel }) => {
  const { t } = useTranslation();
  const { groupId } = useParams();
  const client = useApolloClient();
  const { groupsFeatureFlag } = useFeatureFlagState();
  const { groupGetStartedCompleteList } = useGroupGetStartedState();
  const [addMember] = useMutation(addGroupMemberModalMutation, {
    update(cache, { data }) {
      if (data?.addGroupMember?.error) {
        toast(<Toast message={t('toasts.add-to-group-error')} hasCloseButton />, {
          position: toast.POSITION.BOTTOM_LEFT,
          hideProgressBar: true,
          closeButton: false,
          closeOnClick: true,
          autoClose: 5000,
        });
        client.query({
          query: AddMemberModalQuery,
          variables: { groupId },
          fetchPolicy: 'network-only',
        });
      }
    },
  });

  const { error, data } = useQuery(AddMemberModalQuery, {
    fetchPolicy: 'cache-and-network',
    variables: {
      groupId: groupId,
    },
  });

  if (data === undefined) {
    return (
      <>
        <div className={styles.header}>
          <h3 className={styles.heading}>Add to Group</h3>
        </div>

        <div className={styles.content}>
          <SelectItemLoader />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <div className={styles.header}>
          <h3 className={styles.heading}>Add to Group</h3>
        </div>

        <div className={styles.content}>
          <SelectItemLoader />
        </div>
      </>
    );
  }

  const addToGroup = (id: string, email: string, name: string, avatarUrl: string) => {
    const readGroup: any = client.readQuery({
      query: GroupPeopleQuery,
      variables: {
        groupId: groupId,
      },
    });
    const existingMembers = readGroup.groupById.members;
    const newMembers = produce(existingMembers, (draft: any) => {
      draft[existingMembers.length] = {
        id: id,
        name: name,
        email: email,
        avatarUrl: avatarUrl,
        __typename: 'GroupMember',
      };
    });
    readGroup.groupById.members = newMembers;
    client.writeQuery({
      query: GroupPeopleQuery,
      data: readGroup,
    });
    addMember({
      variables: { groupId: groupId, personId: id },
    }).then(() => {
      client
        .query({
          query: GroupGetStartedConditionQuery,
          variables: { groupId: groupId },
          fetchPolicy: 'network-only',
        })
        .then((data: any) => {
          //@ts-ignore
          const groupGetStartedComplete = groupGetStartedCompleteList.includes(groupId);
          const reFetchGetStarted =
            data.data.groupById?.activityFeed.pageInfo.total < 4 &&
            data.data.groupById?.members.length > 1 &&
            data.data.groupById?.haveNodesInCommon &&
            data.data.groupById?.groupType === GroupTypes.CUSTOM &&
            !groupGetStartedComplete;

          if (reFetchGetStarted) {
            client
              .query({
                query: GroupGetStartedFeedQuery,
                variables: { groupId, includeGroups: groupsFeatureFlag, first: 10, cursor: '' },
                fetchPolicy: 'network-only',
              })
              .catch((error) => {
                Logger.error(error);
              });
          }
        })
        .catch((error) => {
          Logger.error(error);
        });
    });
  };

  // Organization Members that haven't already been added to this group
  const filteredOrganizationMembers = data.me.orgGroup.members.filter(
    (x: any) => !data.groupById.members.map((y: any) => y.email).includes(x.email)
  );

  // OtherPeople that haven't already been added to this group
  const filteredOtherPeople = data.me.otherPeople.filter(
    (x: any) => !data.groupById.members.map((y: any) => y.email).includes(x.email)
  );

  return (
    <>
      <div className={styles.header}>
        <h3 className={styles.heading}>Add to Group</h3>
      </div>

      <div className={styles.content}>
        <div className={styles.sectionHeading}>{data.me.organization.name}</div>
        <TransitionGroup className={styles.transitionGroup}>
          {filteredOrganizationMembers.map((contact: any, key: number) => (
            <CSSTransition
              className={styles.transition}
              classNames={{ exit: styles.transitionExit }}
              key={contact.id + contact.name}
              timeout={ANIMATION_DURATION}
            >
              {/* 
              //@ts-ignore */}
              <SelectItem
                key={key}
                checkbox="Circle"
                checked={false}
                name={contact.name}
                imgURL={contact.avatarUrl}
                onClick={() =>
                  addToGroup(contact.id, contact.email, contact.name, contact.avatarUrl)
                }
              />
            </CSSTransition>
          ))}
        </TransitionGroup>
        <div className={styles.sectionHeading}>Other People</div>
        <TransitionGroup className={styles.transitionGroup}>
          {filteredOtherPeople.map((contact: any, key: number) => (
            <CSSTransition
              className={styles.transition}
              classNames={{ exit: styles.transitionExit }}
              key={contact.id + contact.name}
              timeout={ANIMATION_DURATION}
            >
              {/* 
            //@ts-ignore */}
              <SelectItem
                key={key}
                checkbox="Circle"
                checked={false}
                name={contact.name}
                imgURL={contact.avatarUrl}
                onClick={() =>
                  addToGroup(contact.id, contact.email, contact.name, contact.avatarUrl)
                }
              />
            </CSSTransition>
          ))}
        </TransitionGroup>
      </div>
    </>
  );
};
